import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Stack,
} from '@mui/material'
import { shipmentService, accountService, driverService, vehicleService } from '../api/adminServices'
import { ResizableTableCell } from '../components/ResizableTable'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import PageRefreshButton from '../components/PageRefreshButton'
import {
  buildEntityLookups,
  formatColumnHeader,
} from '../utils/entityDisplay'
import AccountDetailDialog from '../components/AccountDetailDialog'
import { renderEntityCell } from '../components/EntityAccountCell'

type TimeWindow = 'LAST_30_DAYS' | 'TODAY' | 'NEXT_7_DAYS' | 'NEXT_30_DAYS'
type JobStatus = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      style={value === index ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</Box>}
    </div>
  )
}

export default function JobsPage() {
  const getLocalizedLabel = useLocalizedLabel()
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('TODAY')
  const [activeTab, setActiveTab] = useState(0)
  const [accountDetailId, setAccountDetailId] = useState<string | null>(null)

  const statusTabs: JobStatus[] = ['PLANNED', 'IN_PROGRESS', 'COMPLETED']

  // Format date to ISO_LOCAL_DATE_TIME (YYYY-MM-DDTHH:mm:ss) without timezone or milliseconds
  const formatToIsoLocalDateTime = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  // Calculate date range based on time window
  const getDateRange = (window: TimeWindow): { from: string; to: string } => {
    const now = new Date()
    let from: Date
    let to: Date

    switch (window) {
      case 'LAST_30_DAYS':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
        break
      case 'TODAY':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
        break
      case 'NEXT_7_DAYS':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 23, 59, 59)
        break
      case 'NEXT_30_DAYS':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30, 23, 59, 59)
        break
      default:
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
    }

    return {
      from: formatToIsoLocalDateTime(from),
      to: formatToIsoLocalDateTime(to),
    }
  }

  const dateRange = getDateRange(timeWindow)

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-jobs'],
    queryFn: () => accountService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const { data: driversData } = useQuery({
    queryKey: ['drivers-for-jobs'],
    queryFn: () => driverService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles-for-jobs'],
    queryFn: () => vehicleService.getAll(0, 2000),
    staleTime: 5 * 60 * 1000,
  })

  const entityLookups = useMemo(
    () => buildEntityLookups(accountsData, driversData, vehiclesData),
    [accountsData, driversData, vehiclesData]
  )

  // Fetch all jobs for the time window (no status filter)
  const {
    data: allJobs,
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['jobs', dateRange.from, dateRange.to],
    queryFn: async () => {
      const result = await shipmentService.getJobsByTimeWindow(dateRange.from, dateRange.to)
      return Array.isArray(result) ? result : result?.content || result?.data || []
    },
    retry: 1,
  })

  // Filter by current status on client side
  const currentStatus = statusTabs[activeTab]
  const jobs = allJobs
    ? allJobs.filter((job: any) => {
        const jobStatus = String(job.status || job.jobStatus || '').toUpperCase()
        return jobStatus === currentStatus
      })
    : []

  const handleTimeWindowChange = (_event: React.MouseEvent<HTMLElement>, newValue: TimeWindow | null) => {
    if (newValue !== null) {
      setTimeWindow(newValue)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  // Helper to get enum name from field key
  const getEnumNameFromKey = (fieldKey: string): string | null => {
    const lowerKey = fieldKey.toLowerCase()
    const enumFieldMap: Record<string, string> = {
      status: 'jobStatus',
      jobstatus: 'jobStatus',
      job_status: 'jobStatus',
      cargotype: 'cargoType',
      cargo_type: 'cargoType',
      paymentterms: 'paymentTerms',
      payment_terms: 'paymentTerms',
      currency: 'currency',
    }
    return enumFieldMap[lowerKey] || null
  }

  // Get table columns from first item if available
  const getColumns = () => {
    if (!jobs || jobs.length === 0) return []
    const firstItem = jobs[0]
    return Object.keys(firstItem).filter(
      (key) =>
        !key.includes('password') &&
        !key.includes('token') &&
        (typeof firstItem[key] !== 'object' || firstItem[key] === null || Array.isArray(firstItem[key]))
    )
  }

  const columns = getColumns()

  const formatValue = (key: string, value: any): any => {
    // Handle null, undefined, empty string, and "null" string
    if (value === null || value === undefined || value === '' || value === 'null') return '-'

    const entityCell = renderEntityCell(key, value, entityLookups, setAccountDetailId)
    if (entityCell) return entityCell

    // Handle boolean
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'

    // Handle arrays
    if (Array.isArray(value)) {
      return value.join(', ')
    }

    // Handle date-time strings
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return new Date(value).toLocaleString()
    }

    // Handle enum fields - try to localize
    const enumName = getEnumNameFromKey(key)
    if (enumName) {
      const localized = getLocalizedLabel(enumName, String(value))
      if (localized !== String(value)) {
        // Return as Chip for enum values
        return (
          <Chip
            label={localized}
            size="small"
            color={
              String(value).toUpperCase() === 'COMPLETED' || String(value).toUpperCase() === 'VERIFIED'
                ? 'success'
                : String(value).toUpperCase() === 'IN_PROGRESS' || String(value).toUpperCase() === 'PLANNED'
                ? 'info'
                : 'default'
            }
          />
        )
      }
    }

    // Handle status field specifically
    if (key.toLowerCase() === 'status' || key.toLowerCase() === 'jobstatus') {
      const statusValue = String(value)
      const localizedStatus = getLocalizedLabel('jobStatus', statusValue)
      return (
        <Chip
          label={localizedStatus}
          size="small"
          color={
            statusValue.toUpperCase() === 'COMPLETED'
              ? 'success'
              : statusValue.toUpperCase() === 'IN_PROGRESS'
              ? 'info'
              : statusValue.toUpperCase() === 'PLANNED'
              ? 'default'
              : 'default'
          }
        />
      )
    }

    return String(value)
  }

  // Calculate counts for each status tab
  const getStatusCount = (status: JobStatus): number => {
    if (!allJobs) return 0
    return allJobs.filter((job: any) => {
      const jobStatus = String(job.status || job.jobStatus || '').toUpperCase()
      return jobStatus === status
    }).length
  }

  return (
    <Box className="animate-fade-in" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Time Window
              </Typography>
              <ToggleButtonGroup
                value={timeWindow}
                exclusive
                onChange={handleTimeWindowChange}
                aria-label="time window filter"
                size="small"
              >
                <ToggleButton value="LAST_30_DAYS" aria-label="last 30 days">
                  Last 30 Days
                </ToggleButton>
                <ToggleButton value="TODAY" aria-label="today">
                  Today
                </ToggleButton>
                <ToggleButton value="NEXT_7_DAYS" aria-label="next 7 days">
                  Next 7 Days
                </ToggleButton>
                <ToggleButton value="NEXT_30_DAYS" aria-label="next 30 days">
                  Next 30 Days
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
          </Stack>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {statusTabs.map((status) => {
            const count = getStatusCount(status)
            const label = getLocalizedLabel('jobStatus', status) || status
            return (
              <Tab
                key={status}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{label}</span>
                    <Chip
                      label={count}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '0.75rem',
                        bgcolor: 'grey.300',
                        color: 'text.primary',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                }
              />
            )
          })}
        </Tabs>

        {/* Tab Panels */}
        {statusTabs.map((status, index) => (
          <TabPanel key={status} value={activeTab} index={index}>
            {isLoading ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <TableSkeleton rows={8} columns={8} />
              </Box>
            ) : isError ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ErrorCard
                  title="Failed to load jobs"
                  message={(error as Error)?.message}
                  onRetry={() => refetch()}
                />
              </Box>
            ) : !jobs || jobs.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <EmptyState
                  title="No jobs found"
                  description={`No ${getLocalizedLabel('jobStatus', status) || status} jobs in the selected time window.`}
                />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  maxHeight: 'calc(100vh - 300px)',
                  overflow: 'auto',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: 'background.paper',
                      '& th': {
                        backgroundColor: 'background.paper',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                      },
                    }}
                  >
                    <TableRow>
                      {columns.map((column) => (
                        <ResizableTableCell key={column}>
                          {formatColumnHeader(column)}
                        </ResizableTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job: any, idx: number) => (
                      <TableRow key={job.id || idx} hover>
                        {columns.map((column) => (
                          <TableCell key={column}>
                            {typeof formatValue(column, job[column]) === 'string' ||
                            typeof formatValue(column, job[column]) === 'number' ? (
                              formatValue(column, job[column])
                            ) : (
                              formatValue(column, job[column])
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        ))}
      </Paper>

      <AccountDetailDialog
        accountId={accountDetailId}
        open={!!accountDetailId}
        onClose={() => setAccountDetailId(null)}
      />
    </Box>
  )
}

